/**
 * Utility functions for Draft.js content manipulation in the email editor.
 * Handles entity remapping and content state operations for safe paste handling.
 */

import {
  CharacterMetadata,
  ContentBlock,
  ContentState,
  Modifier,
} from "draft-js";

/**
 * Preprocesses HTML for pasting by converting newline characters to <br> tags.
 *
 * Draft.js clipboard format uses literal \n characters for line breaks inside
 * inline elements. This converts them to <br> tags so convertFromHTML can
 * process them (though it treats them as soft breaks within a single block).
 *
 * Note: We intentionally don't try to split content into separate blocks
 * because that would break inline formatting elements (like <strong>) that
 * span across line breaks.
 *
 * @param html - Raw HTML string from clipboard
 * @returns HTML with newlines converted to <br> tags
 */
export function preprocessHtmlForPaste(html: string): string {
  // Normalize line endings to \n first
  let normalizedHtml = html.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  // Paragraph breaks: two+ newlines (with optional whitespace between) become <br><br>
  normalizedHtml = normalizedHtml.replace(/\n\s*\n/g, "<br><br>");
  // Single newlines become single <br>
  normalizedHtml = normalizedHtml.replace(/\n/g, "<br>");

  return normalizedHtml;
}

/**
 * Type for the BlockMap expected by Modifier.replaceWithFragment.
 * Using Parameters utility to extract the exact type from the function signature.
 */
type BlockMap = Parameters<typeof Modifier.replaceWithFragment>[2];

/**
 * Remaps entities from pasted content to the target content state.
 *
 * When pasting HTML content, entity keys in the pasted content reference a different
 * ContentState's entity map. This function:
 * 1. Creates new entities in the target ContentState for each entity in pasted content
 * 2. Updates the block character metadata to reference the new entity keys
 *
 * @param pastedContent - ContentState created from pasted HTML
 * @param targetContent - The current editor's ContentState to merge into
 * @returns Object containing the updated target content and remapped blocks ready for insertion
 */
export const remapEntitiesForPaste = (
  pastedContent: ContentState,
  targetContent: ContentState
): { content: ContentState; remappedBlocks: BlockMap } => {
  let updatedContent = targetContent;
  const entityKeyMap: Record<string, string> = {};

  // Create new entities in target content for each entity in pasted content
  pastedContent.getBlockMap().forEach((block) => {
    if (!block) return;
    block.findEntityRanges(
      (char) => char.getEntity() !== null,
      (start) => {
        const entityKey = block.getEntityAt(start);
        if (entityKey && !entityKeyMap[entityKey]) {
          const entity = pastedContent.getEntity(entityKey);
          updatedContent = updatedContent.createEntity(
            entity.getType(),
            entity.getMutability(),
            entity.getData()
          );
          entityKeyMap[entityKey] = updatedContent.getLastCreatedEntityKey();
        }
      }
    );
  });

  // Remap entity keys in the blocks
  const remappedBlocks = pastedContent
    .getBlockMap()
    .map((block) => {
      if (!block) return block;
      let newCharList = block.getCharacterList();
      block.findEntityRanges(
        (char) => char.getEntity() !== null,
        (start, end) => {
          const oldKey = block.getEntityAt(start);
          if (oldKey && entityKeyMap[oldKey]) {
            for (let i = start; i < end; i++) {
              const char = newCharList.get(i);
              if (char) {
                newCharList = newCharList.set(
                  i,
                  CharacterMetadata.applyEntity(char, entityKeyMap[oldKey])
                );
              }
            }
          }
        }
      );
      return block.set("characterList", newCharList) as ContentBlock;
    })
    .toOrderedMap() as BlockMap;

  return { content: updatedContent, remappedBlocks };
};
